import { ChangeDetectionStrategy, Component, computed, inject, input, signal } from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { environment } from '@environment';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { Store } from '@ngrx/store';
import {
  blogAdvicesEnabled,
  blogFaqEnabled,
  blogFinancialAidEnabled,
} from '@stores/features-flags/features-flags-ngrx.selector';
import { MenuComponent } from '@wizbii-money/angular';
import { jwtDelete } from '@wizbii-utils/angular/stores';
import { trackEvent } from '@wizbii/utils/tracking';

export interface NavItem {
  label: string;
  icon: string;
  link?: string | string[];
  click: () => void;
}

@Component({
  selector: 'app-mobile-menu',
  imports: [RouterLink, RouterLinkActive, MatIcon, MenuComponent],
  template: `
    <!-- eslint-disable @angular-eslint/template/no-call-expression -->

    <nav class="menu-nav">
      <ul class="menu-nav__list" role="menubar">
        @for (item of navItems(); track $index) {
          <li class="nav-item" role="none">
            @if (item.link) {
              <a [routerLink]="item.link" (click)="item.click()" routerLinkActive="link-active" role="menuitem">
                <mat-icon class="nav-item__icon" [svgIcon]="item.icon" />
                {{ item.label }}
              </a>
            } @else {
              <button (click)="item.click()" type="button" role="menuitem">
                <mat-icon [svgIcon]="item.icon" />
                {{ item.label }}
              </button>
            }
          </li>
        }
        <li class="nav-item" role="none">
          <wzb-mny-menu
            [items]="menuItems()"
            (menuClosed)="isMenuOpened.set(false)"
            (menuOpened)="isMenuOpened.set(true)"
          >
            <div class="nav-item__menu" [class.link-active]="isMenuOpened()" type="button" role="menuitem">
              <mat-icon svgIcon="subject" />
              <ng-container i18n="@@mobile-menu.title"> Menu </ng-container>
            </div>
          </wzb-mny-menu>
        </li>
      </ul>
    </nav>
  `,
  styles: [
    `
      @use 'breakpoints' as br;
      @use 'colors/colors' as colors;

      .menu-nav {
        position: fixed;
        background-color: #fff;
        box-shadow: 0 0.5rem 1.5625rem 0 rgb(0 0 0 / 25%);
        bottom: 0;
        left: 0;
        right: 0;
        z-index: 100;
        padding: 0 1rem 1rem;

        @include br.notHandset {
          display: none;
        }

        &__list {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          justify-content: space-around;
          margin: 0;
        }
      }
      .nav-item {
        display: grid;

        &__icon {
          width: 1.5rem;
          height: 1.5rem;
        }

        &::before {
          content: none;
        }

        a,
        button,
        &__menu {
          display: flex;
          gap: 0.25rem;
          align-items: center;
          flex-direction: column;
          text-decoration: none;
          border: none;
          padding: 1rem 0 0;
          color: colors.$grey-7;
          font-size: 0.6875rem;
          position: relative;
          transition: all 0.2s ease;
          text-align: center;
        }
      }

      .link-active {
        color: colors.$wzb-black !important;
        font-weight: bold;

        &::before {
          content: '';
          height: 0.25rem;
          background-color: colors.$wzb-light-blue;
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          border-radius: 0.5rem;
        }
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileMenuComponent {
  readonly #store = inject(Store);

  readonly navItems = input<NavItem[]>();
  readonly isMenuOpened = signal(false);

  readonly blogAdvicesEnabled = this.#store.selectSignal(blogAdvicesEnabled);
  readonly blogFinancialAidEnabled = this.#store.selectSignal(blogFinancialAidEnabled);
  readonly blogFaqEnabled = this.#store.selectSignal(blogFaqEnabled);
  readonly baseHref = environment.baseHref;

  accountUrl = environment.urls.account.base;
  menuItems = computed<any>(() => {
    const blogLinks = [
      ...(this.blogAdvicesEnabled()
        ? [
            {
              type: 'link',
              label: $localize`:@@money.mobile-menu.advices:Nos conseils`,
              href: `${this.baseHref}${FeaturesRoutingEnum.Advices}`,
            },
          ]
        : []),
      ...(this.blogFinancialAidEnabled()
        ? [
            {
              type: 'link',
              label: $localize`:@@money.mobile-menu.helps-directory:Annuaire des aides`,
              href: `${this.baseHref}${FeaturesRoutingEnum.HelpsListing}`,
            },
          ]
        : []),
      ...(this.blogFaqEnabled()
        ? [
            {
              type: 'link',
              label: $localize`:@@money.mobile-menu.faq:FAQ`,
              href: `${this.baseHref}${FeaturesRoutingEnum.Faq}`,
            },
          ]
        : []),
    ];

    return [
      [
        {
          type: 'link',
          label: $localize`:@@money.mobile-menu.profile:Mon profil`,
          href: this.accountUrl,
          isExternal: true,
        },
      ],
      blogLinks,
      [
        {
          type: 'button',
          label: $localize`:@@money.mobile-menu.logout:Déconnexion`,
          extraClass: 'menu__item--signout',
          click: () => {
            this.#store.dispatch(jwtDelete());
            window.open(`${environment.urls.account.logout}${window.location.origin}`, '_self');
          },
        },
      ],
    ];
  });

  trackEvent = trackEvent;
  FeaturesRoutingEnum = FeaturesRoutingEnum;
}
